import { useState, useEffect } from 'react';
import {
    FolderKanban,
    Search,
    Loader2,
    AlertCircle,
    FileText,
    Download,
    Calendar,
    Users,
    HardDrive,
    FileSpreadsheet,
    FileCode,
    FileArchive,
    CheckCircle,
    Sparkles,
    Filter
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { SkeletonCourseGrid } from '../../../components/common/SkeletonLoader';

interface StudentProjectItem {
    id: number;
    title: string;
    description: string | null;
    file_path: string;
    original_file_name: string;
    mime_type: string | null;
    file_size: number | null;
    file_extension: string;
    formatted_file_size: string | null;
    cohort_id: string;
    created_at: string;
    cohort?: {
        id: string;
        name: string;
    };
    creator?: {
        id: number;
        name: string;
    };
}

export default function StudentProjects() {
    const [projects, setProjects] = useState<StudentProjectItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCohort, setSelectedCohort] = useState<string>('all');
    const [downloadingId, setDownloadingId] = useState<number | null>(null);

    const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

    const fetchStudentProjects = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/student/projects`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || 'Failed to fetch cohort projects.');
            }

            const data = await response.json();
            setProjects(data);
            setError(null);
        } catch (err: any) {
            console.error('Fetch Projects Error:', err);
            setError(err.message || 'Unable to load projects at this time.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStudentProjects();
    }, []);

    const handleDownload = async (project: StudentProjectItem) => {
        try {
            setDownloadingId(project.id);
            toast.loading(`Downloading ${project.original_file_name || project.title}...`, { id: 'student-dl' });

            const response = await fetch(`${API_URL}/projects/${project.id}/download`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => null);
                throw new Error(errData?.message || 'Download failed or file not accessible.');
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = project.original_file_name || `${project.title}.${project.file_extension || 'bin'}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            toast.success('Project downloaded successfully!', { id: 'student-dl' });
        } catch (err: any) {
            console.error('Download Error:', err);
            toast.error(err.message || 'Error downloading project file.', { id: 'student-dl' });
        } finally {
            setDownloadingId(null);
        }
    };

    const getFileBadge = (ext: string) => {
        const lower = (ext || '').toLowerCase();
        if (['xls', 'xlsx', 'csv'].includes(lower)) {
            return {
                icon: FileSpreadsheet,
                bg: 'bg-emerald-50 dark:bg-emerald-950/30',
                color: 'text-emerald-600 dark:text-emerald-400',
                borderColor: 'border-emerald-200 dark:border-emerald-800/40',
                label: lower.toUpperCase() || 'EXCEL'
            };
        }
        if (lower === 'pdf') {
            return {
                icon: FileText,
                bg: 'bg-rose-50 dark:bg-rose-950/30',
                color: 'text-rose-600 dark:text-rose-400',
                borderColor: 'border-rose-200 dark:border-rose-800/40',
                label: 'PDF'
            };
        }
        if (['doc', 'docx'].includes(lower)) {
            return {
                icon: FileText,
                bg: 'bg-blue-50 dark:bg-blue-950/30',
                color: 'text-blue-600 dark:text-blue-400',
                borderColor: 'border-blue-200 dark:border-blue-800/40',
                label: lower.toUpperCase()
            };
        }
        if (['ppt', 'pptx'].includes(lower)) {
            return {
                icon: FileText,
                bg: 'bg-amber-50 dark:bg-amber-950/30',
                color: 'text-amber-600 dark:text-amber-400',
                borderColor: 'border-amber-200 dark:border-amber-800/40',
                label: lower.toUpperCase()
            };
        }
        if (['zip', 'rar', '7z'].includes(lower)) {
            return {
                icon: FileArchive,
                bg: 'bg-purple-50 dark:bg-purple-950/30',
                color: 'text-purple-600 dark:text-purple-400',
                borderColor: 'border-purple-200 dark:border-purple-800/40',
                label: 'ARCHIVE'
            };
        }
        return {
            icon: FileCode,
            bg: 'bg-slate-50 dark:bg-slate-800',
            color: 'text-slate-600 dark:text-slate-300',
            borderColor: 'border-slate-200 dark:border-slate-700',
            label: lower.toUpperCase() || 'DOCUMENT'
        };
    };

    const cohortList = Array.from(
        new Map(
            projects
                .filter((p) => p.cohort)
                .map((p) => [p.cohort_id, p.cohort!])
        ).values()
    );

    const filteredProjects = projects.filter((p) => {
        const matchesSearch =
            p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.original_file_name && p.original_file_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.cohort?.name && p.cohort.name.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesCohort = selectedCohort === 'all' || p.cohort_id === selectedCohort;

        return matchesSearch && matchesCohort;
    });

    return (
        <div className="space-y-6 sm:space-y-10 pb-16">
            {/* Header */}
            <header className="max-w-3xl animate-fade-in-up">
                <div className="flex items-center gap-2.5 mb-3 sm:mb-4">
                    <div className="p-2 sm:p-2.5 bg-brand-emerald/10 text-brand-emerald rounded-xl">
                        <FolderKanban size={20} />
                    </div>
                    <span className="text-brand-emerald font-black text-xs uppercase tracking-widest">
                        Practical Workbooks & Exercises
                    </span>
                </div>
                <h1 className="text-2xl sm:text-4xl md:text-5xl font-black text-brand-charcoal dark:text-white tracking-tight mb-2 sm:mb-4">
                    Cohort <span className="text-brand-emerald">Projects</span>
                </h1>
                <p className="text-brand-muted font-medium text-sm sm:text-base md:text-lg leading-relaxed">
                    Access and download assigned cohort workbooks, scheduling templates, datasets, and project guidelines.
                </p>
            </header>

            {/* Filter & Search Bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 animate-fade-in-up">
                <div className="relative flex-1">
                    <Search className="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-brand-muted" size={18} />
                    <input
                        type="text"
                        className="w-full h-12 sm:h-14 pl-12 sm:pl-14 pr-4 sm:pr-6 bg-white dark:bg-brand-charcoal border border-brand-border rounded-xl sm:rounded-2xl focus:outline-none focus:ring-4 focus:ring-brand-emerald/10 focus:border-brand-emerald transition-all text-brand-charcoal dark:text-white font-bold text-base sm:text-sm shadow-xs"
                        placeholder="Search projects by title, keyword, or filename..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {cohortList.length > 1 && (
                    <div className="w-full sm:w-auto sm:min-w-[200px]">
                        <select
                            className="w-full h-12 sm:h-14 px-4 bg-white dark:bg-brand-charcoal border border-brand-border rounded-xl sm:rounded-2xl focus:outline-none focus:ring-4 focus:ring-brand-emerald/10 focus:border-brand-emerald transition-all text-brand-charcoal dark:text-white font-bold text-base sm:text-sm shadow-xs cursor-pointer"
                            value={selectedCohort}
                            onChange={(e) => setSelectedCohort(e.target.value)}
                        >
                            <option value="all">All Cohorts ({projects.length})</option>
                            {cohortList.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Content Area */}
            {loading ? (
                <SkeletonCourseGrid />
            ) : error ? (
                <div className="bg-white dark:bg-brand-charcoal p-6 sm:p-12 rounded-2xl sm:rounded-3xl border border-red-100 dark:border-red-900/30 text-center space-y-4 sm:space-y-5 shadow-sm">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-red-50 dark:bg-red-900/20 rounded-2xl flex items-center justify-center mx-auto text-red-500">
                        <AlertCircle size={28} />
                    </div>
                    <div>
                        <h2 className="text-xl sm:text-2xl font-black text-brand-charcoal dark:text-white">Unable to Load Projects</h2>
                        <p className="text-brand-muted font-medium mt-1 text-xs sm:text-sm">{error}</p>
                    </div>
                    <button
                        onClick={fetchStudentProjects}
                        className="px-6 py-3 bg-brand-emerald text-white rounded-xl font-bold text-sm hover:brightness-110 transition-all cursor-pointer"
                    >
                        Try Again
                    </button>
                </div>
            ) : filteredProjects.length === 0 ? (
                <div className="bg-white dark:bg-brand-charcoal p-6 sm:p-12 md:p-16 rounded-2xl sm:rounded-3xl border border-brand-border text-center space-y-4 shadow-xs">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl flex items-center justify-center mx-auto text-brand-emerald">
                        <FolderKanban size={28} />
                    </div>
                    <div>
                        <h3 className="text-lg sm:text-xl font-black text-brand-charcoal dark:text-white mb-1">
                            {searchTerm ? 'No Matching Projects' : 'No Projects Assigned Yet'}
                        </h3>
                        <p className="text-brand-muted font-medium text-xs sm:text-sm max-w-md mx-auto">
                            {searchTerm
                                ? 'No project files match your search query. Try clearing your search term.'
                                : 'Your instructor has not uploaded project files for your enrolled cohorts yet. Check back soon!'}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {filteredProjects.map((project) => {
                        const badge = getFileBadge(project.file_extension);
                        const BadgeIcon = badge.icon;
                        const formattedDate = project.created_at
                            ? new Date(project.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                            })
                            : 'Recent';

                        const isDownloading = downloadingId === project.id;

                        return (
                            <div
                                key={project.id}
                                className="group bg-white dark:bg-brand-charcoal border border-brand-border hover:border-brand-emerald/40 rounded-2xl sm:rounded-3xl p-5 sm:p-6 flex flex-col justify-between transition-all duration-300 hover:shadow-xl hover:shadow-brand-emerald/5 hover:-translate-y-1"
                            >
                                <div>
                                    {/* Top Metadata */}
                                    <div className="flex items-start justify-between gap-3 mb-4">
                                        <div
                                            className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl ${badge.bg} ${badge.color} border ${badge.borderColor} flex items-center justify-center flex-shrink-0 shadow-xs`}
                                        >
                                            <BadgeIcon size={24} />
                                        </div>
                                        <div className="text-right">
                                            <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-brand-emerald/10 text-brand-emerald">
                                                <Users size={12} />
                                                <span className="truncate max-w-[120px] sm:max-w-[140px]">
                                                    {project.cohort?.name || 'Cohort Project'}
                                                </span>
                                            </span>
                                        </div>
                                    </div>

                                    {/* Project Title */}
                                    <h3 className="text-base sm:text-lg font-black text-brand-charcoal dark:text-white group-hover:text-brand-emerald transition-colors line-clamp-2 mb-2">
                                        {project.title}
                                    </h3>

                                    {/* Description */}
                                    {project.description ? (
                                        <p className="text-brand-muted text-xs sm:text-sm font-medium line-clamp-3 mb-4 sm:mb-5 leading-relaxed">
                                            {project.description}
                                        </p>
                                    ) : (
                                        <p className="text-brand-muted/70 text-xs italic font-medium mb-4 sm:mb-5">
                                            Download this project workbook to complete your practical scheduling exercise.
                                        </p>
                                    )}

                                    {/* File details pill */}
                                    <div className="p-2.5 sm:p-3 bg-brand-beige/50 dark:bg-white/5 rounded-xl sm:rounded-2xl mb-4 sm:mb-6 space-y-1 text-xs">
                                        <div className="flex items-center justify-between text-brand-charcoal dark:text-gray-200 font-bold">
                                            <span className="truncate mr-2" title={project.original_file_name}>
                                                {project.original_file_name || 'Project Document'}
                                            </span>
                                            <span className="text-brand-emerald flex-shrink-0 font-extrabold uppercase">
                                                {badge.label}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-brand-muted font-medium">
                                            <span>Size: {project.formatted_file_size || 'Standard'}</span>
                                            <span>Uploaded: {formattedDate}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Download Button */}
                                <button
                                    type="button"
                                    onClick={() => handleDownload(project)}
                                    disabled={isDownloading}
                                    className="w-full py-3 sm:py-3.5 px-4 sm:px-5 bg-brand-emerald hover:bg-emerald-600 active:scale-98 text-white rounded-xl sm:rounded-2xl font-black text-sm flex items-center justify-center gap-2.5 transition-all shadow-md shadow-brand-emerald/20 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {isDownloading ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            <span>Downloading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Download size={18} strokeWidth={2.5} />
                                            <span>Download Project</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
